import type { AxiosResponse } from "axios";
import type { LocalDate } from "@js-joda/core";
import { API_ROOT } from "services/baseURLs.config";
import { CompanyGigShift } from "model/Gig";
import type { AbsenceType, ScheduleStatus } from "model/Schedule";
import { ShiftDetails } from "web-apps/company/components/schedule/components/common/modals/shiftDetailsModal.component";
import { ScheduleApiData } from "web-apps/company/components/schedule/types/schedule.types";
import { formatLocalDateAsApiDate } from "web-apps/company/utils/timeAndDate";
import { axiosRequest } from "../api.utils";

export type ShiftData = {
  startTime: string;
  endTime: string;
  breakTime: number;
  breakStartTime: string;
  breakEndTime: string;
  deductionAmount: number;
};

export interface AvailableReplacer {
  id: number;
  firstName: string;
  lastName: string;
  externalId: string;
}

export interface ResolutionDetails {
  timeReportId: number;
  status: string;
  resolution: AbsenceType;
}

export interface ShiftStatusResponse {
  companyUnitId: number | null;
  companyUnitName: string | null;
  workGroupId: number | null;
  workGroupName: string | null;
  workerId: number | null;
  workerName: string | null;
  gigId: number;
  gigType: string;
  gigShiftId: number;
  startTime: string;
  endTime: string;
  status: ScheduleStatus;
  timeReportIds: number[];
  details: ResolutionDetails[];
}

export type EditScheduleShiftResponse = CompanyGigShift & {
  id: number;
  gigId: number;
  breakTime: number;
  breakStartTime: string;
  breakEndTime: string;
  deductionAmount: number;
};

export type ScheduleSortBy = "worker" | "workGroup";

interface GetScheduleForDayParams {
  readonly companyId: number;
  readonly date: LocalDate;
}

const getSchedule = async (
  companyId: number,
  fromDate: string,
  toDate: string,
  page?: number,
  pageSize?: number,
  sortBy?: ScheduleSortBy,
  workGroupIds?: number[],
  workerIds?: number[],
): Promise<AxiosResponse<ScheduleApiData>> => {
  const params: {
    page?: number;
    pageSize?: number;
    sortBy?: ScheduleSortBy;
    workGroupIds?: string;
    workerIds?: string;
  } = {
    page,
    pageSize,
    sortBy,
    workGroupIds:
      workGroupIds && workGroupIds.length > 0
        ? workGroupIds.join(",")
        : undefined,
    workerIds:
      workerIds && workerIds.length > 0 ? workerIds.join(",") : undefined,
  };
  return await axiosRequest.auth.get(
    `${API_ROOT}/companies/${companyId}/schedule/${fromDate}-${toDate}`,
    {
      params,
    },
  );
};

const getScheduleForDay = async ({
  companyId,
  date,
}: GetScheduleForDayParams): Promise<AxiosResponse<ScheduleApiData>> => {
  const apiDate = formatLocalDateAsApiDate(date);
  return await getSchedule(companyId, apiDate, apiDate, 0, 50, "worker");
};

const getShiftDetails = async (
  companyId: number,
  gigId: number,
  gigShiftId: number,
): Promise<AxiosResponse<ShiftDetails>> => {
  return await axiosRequest.auth.get(
    `${API_ROOT}/companies/${companyId}/gigs/${gigId}/gigShifts/${gigShiftId}`,
  );
};

const editShift = async (
  companyId: number,
  gigId: number,
  gigShiftId: number,
  data: ShiftData,
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/gigs/${gigId}/gigShifts/${gigShiftId}`,
    { data },
  );
};

const getShiftStatus = async (
  companyId: number,
  shiftId: number,
  workerId: number,
): Promise<AxiosResponse<ShiftStatusResponse>> => {
  return await axiosRequest.auth.get(
    `${API_ROOT}/companies/${companyId}/schedule/shifts/${shiftId}/workers/${workerId}`,
  );
};

const editScheduleShift = async (
  companyId: number,
  gigId: number,
  gigShiftId: number,
  data: ShiftData,
): Promise<AxiosResponse<EditScheduleShiftResponse>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/gigs/${gigId}/gigShifts/${gigShiftId}`,
    data,
  );
};

const getAvailableReplacers = async (
  companyId: number,
  workerId: number,
  workGroupId: number,
  startTime: string,
  endDate: string,
): Promise<AxiosResponse<AvailableReplacer[]>> => {
  const query = new URLSearchParams({
    workerId: String(workerId),
    workGroupId: String(workGroupId),
    startTime,
    endDate,
  });
  return await axiosRequest.auth.get(
    `${API_ROOT}/companies/${companyId}/workers/list/available?${query.toString()}`,
  );
};

export const schedule = {
  getSchedule,
  getScheduleForDay,
  getShiftDetails,
  editShift,
  getShiftStatus,
  getAvailableReplacers,
  editScheduleShift,
};
